/**
 * LiveEventDashboard - Real-time event monitoring dashboard
 * Auto-refreshes every 12 seconds with live stats
 */
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { getToken } from '@/lib/authToken';
import {
  Users, UserCheck, Clock, Utensils, Shield, Activity,
  TrendingUp, RefreshCw, Wifi, WifiOff, ChevronRight,
  Coffee, Sun, Moon, BarChart3, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MEAL_ICONS = {
  breakfast: Coffee,
  lunch: Sun,
  dinner: Moon
};

const ACTIVITY_COLORS = {
  checkin: 'bg-emerald-500',
  meal: 'bg-amber-500',
  security: 'bg-red-500'
};

const StatCard = ({ title, value, subtitle, icon: Icon, color, trend, large }) => (
  <Card className={`relative overflow-hidden ${large ? 'col-span-2' : ''}`} data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{title}</p>
          <p className={`${large ? 'text-4xl' : 'text-2xl'} font-bold text-slate-900 mt-1`}>{value}</p>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg bg-${color}-100 flex items-center justify-center flex-shrink-0`}>
          <Icon className={`h-5 w-5 text-${color}-600`} />
        </div>
      </div>
      {trend !== undefined && (
        <div className="flex items-center gap-1 mt-2">
          {trend >= 0 ? (
            <ArrowUpRight className="h-3 w-3 text-emerald-500" />
          ) : (
            <ArrowDownRight className="h-3 w-3 text-red-500" />
          )}
          <span className={`text-xs font-medium ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {Math.abs(trend)} today
          </span>
        </div>
      )}
    </CardContent>
  </Card>
);

const MiniBar = ({ value, max, color = 'emerald' }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
      <div
        className={`h-full bg-${color}-500 rounded-full transition-all duration-700`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

const LiveEventDashboard = ({ eventId }) => {
  const [isLive, setIsLive] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const { data: dashboard, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['live-dashboard', eventId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/live-dashboard`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!res.ok) throw new Error('Failed to load dashboard');
      return res.json();
    },
    refetchInterval: isLive ? 12000 : false,
    enabled: !!eventId
  });

  useEffect(() => {
    if (dashboard) setLastRefresh(new Date());
  }, [dashboard]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!dashboard) return null;

  const { checkin, meals, security, recent_activity, hourly_trend, registration_status } = dashboard;
  const maxHourly = Math.max(...(hourly_trend || []).map(h => h.count), 1);

  return (
    <div className="space-y-6" data-testid="live-event-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Live Event Dashboard</h2>
          <p className="text-sm text-slate-500">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            data-testid="refresh-dashboard"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant={isLive ? "default" : "outline"}
            size="sm"
            onClick={() => setIsLive(!isLive)}
            className={isLive ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            data-testid="toggle-live"
          >
            {isLive ? <Wifi className="h-3.5 w-3.5 mr-1.5" /> : <WifiOff className="h-3.5 w-3.5 mr-1.5" />}
            {isLive ? 'LIVE' : 'Paused'}
          </Button>
        </div>
      </div>

      {/* Main Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Delegates"
          value={checkin?.total || 0}
          icon={Users}
          color="slate"
        />
        <StatCard
          title="Checked In"
          value={checkin?.checked_in || 0}
          subtitle={`${checkin?.percentage || 0}% of total`}
          icon={UserCheck}
          color="emerald"
          trend={checkin?.checked_in_today}
        />
        <StatCard
          title="Pending"
          value={checkin?.pending || 0}
          subtitle="Not yet checked in"
          icon={Clock}
          color="amber"
        />
        <StatCard
          title="Meals Served"
          value={meals?.today_total || 0}
          subtitle="Today"
          icon={Utensils}
          color="orange"
        />
      </div>

      {/* Check-in Progress Bar */}
      <Card data-testid="checkin-progress">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">Check-in Progress</span>
            <span className="text-sm font-bold text-emerald-600">{checkin?.percentage || 0}%</span>
          </div>
          <Progress value={checkin?.percentage || 0} className="h-3" />
          <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
            <span>{checkin?.checked_in || 0} checked in</span>
            <span>{checkin?.pending || 0} remaining</span>
          </div>
        </CardContent>
      </Card>

      {/* Middle Row: Meals + Security + Gates */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Meal Breakdown */}
        <Card data-testid="meal-stats">
          <CardHeader className="pb-2 p-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Utensils className="h-4 w-4 text-amber-500" />
              Meals Today
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            {['breakfast', 'lunch', 'dinner'].map(meal => {
              const MealIcon = MEAL_ICONS[meal];
              const count = meals?.by_type?.[meal] || 0;
              return (
                <div key={meal} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <MealIcon className="h-4 w-4 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium capitalize">{meal}</span>
                      <span className="text-sm font-bold">{count}</span>
                    </div>
                    <MiniBar value={count} max={checkin?.checked_in || 1} color="amber" />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Security */}
        <Card data-testid="security-stats">
          <CardHeader className="pb-2 p-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Shield className="h-4 w-4 text-red-500" />
              Security Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
              <span className="text-sm text-emerald-700">Entries</span>
              <span className="text-lg font-bold text-emerald-700">{security?.entries_today || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <span className="text-sm text-red-700">Exits</span>
              <span className="text-lg font-bold text-red-700">{security?.exits_today || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <span className="text-sm text-blue-700">On Premises</span>
              <span className="text-lg font-bold text-blue-700">{Math.max(0, security?.on_premises || 0)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Gate Stats */}
        <Card data-testid="gate-stats">
          <CardHeader className="pb-2 p-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-indigo-500" />
              Check-in by Gate
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-2">
            {Object.entries(checkin?.by_gate || {}).length > 0 ? (
              Object.entries(checkin.by_gate).map(([gate, count]) => (
                <div key={gate} className="flex items-center justify-between p-2 bg-indigo-50 rounded-lg">
                  <span className="text-sm font-medium text-indigo-700 capitalize">{gate}</span>
                  <Badge className="bg-indigo-100 text-indigo-700">{count}</Badge>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">No gate data yet</p>
            )}
            {/* Registration Status */}
            <div className="mt-3 pt-3 border-t">
              <p className="text-xs font-medium text-slate-500 mb-2">Registration Status</p>
              {Object.entries(registration_status || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between text-xs py-0.5">
                  <span className="capitalize text-slate-600">{status.replace(/_/g, ' ')}</span>
                  <span className="font-medium">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Hourly Trend */}
      <Card data-testid="hourly-trend">
        <CardHeader className="pb-2 p-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-500" />
            Check-in Trend (Last 12 Hours)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="flex items-end gap-1 h-24">
            {(hourly_trend || []).map((hour, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div
                  className="w-full bg-emerald-400 rounded-t transition-all duration-500 min-h-[2px]"
                  style={{ height: `${maxHourly > 0 ? (hour.count / maxHourly) * 80 : 2}px` }}
                  title={`${hour.hour}: ${hour.count} check-ins`}
                />
                <span className="text-[9px] text-slate-400 leading-none">{hour.hour.split(':')[0]}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity Feed */}
      <Card data-testid="recent-activity">
        <CardHeader className="pb-2 p-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Activity className="h-4 w-4 text-blue-500" />
            Recent Activity
          </CardTitle>
          <CardDescription className="text-xs">Live scan log across all checkpoints</CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {(recent_activity || []).length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No activity yet</p>
            ) : (
              recent_activity.map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${ACTIVITY_COLORS[item.type] || 'bg-slate-400'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{item.name}</p>
                    <p className="text-xs text-slate-400 capitalize">{item.detail}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <Badge variant="outline" className="text-[10px]">
                      {item.type}
                    </Badge>
                    {item.timestamp && (
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LiveEventDashboard;
